import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { PlanFeatureOption } from "@/model/interface/PlanFeatureOption";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  useGetPlanFeatureOptionsQuery,
  useGetPricingPlansQuery,
  useGetSubscriptionPlansQuery,
  useCreateSubscriptionPlanMutation,
  useUpdateSubscriptionPlanMutation,
  useDeleteSubscriptionPlanMutation,
} from "@/store/admin_api";
import { PlanPricingMode, PlanVisibility, SubscriptionPlan } from "@/model/interface/SubscriptionPlan";
import React from "react";

const DEFAULT_USAGE_RATES = "default";

const emptyPlan: Partial<SubscriptionPlan> = {
  name: "",
  slug: "",
  monthlyBasePrice: 0,
  annualBasePrice: null,
  includedSeats: 1,
  extraSeatPrice: 0,
  unlimitedSeats: false,
  stripeLookupKey: null,
  visibility: "PUBLIC",
  pricingMode: "LIST",
  pricingPlanId: null,
  perSeatMonthlyPrice: 10,
  includedOutboundCallMinutes: 200,
  includedInboundCallMinutes: null,
  includedOutboundSms: 100,
  includedInboundSms: null,
  trialOutboundCallMinutes: 50,
  trialOutboundSms: 20,
  includedPhoneNumbers: null,
  maxContactLists: null,
  features: [],
  includedAiAgentMinutes: 0,
  trialAiAgentMinutes: 5,
  maxAiAgents: null,
  isActive: true,
  isDefault: false,
  sortOrder: 0,
};

export default function SubscriptionPlansAdmin() {
  const { data: plans, isLoading } = useGetSubscriptionPlansQuery();
  const { data: featureOptions } = useGetPlanFeatureOptionsQuery();
  const { data: pricingPlans } = useGetPricingPlansQuery();

  const featureOptionsByCategory = (featureOptions ?? []).reduce<Record<string, PlanFeatureOption[]>>(
    (grouped, option) => {
      grouped[option.category] = [...(grouped[option.category] ?? []), option];
      return grouped;
    },
    {},
  );
  const [createPlan] = useCreateSubscriptionPlanMutation();
  const [updatePlan] = useUpdateSubscriptionPlanMutation();
  const [deletePlan] = useDeleteSubscriptionPlanMutation();

  const [editing, setEditing] = useState<Partial<SubscriptionPlan> | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const openCreate = () => {
    setEditing({ ...emptyPlan });
    setDialogOpen(true);
  };
  const openEdit = (plan: SubscriptionPlan) => {
    setEditing({ ...plan });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editing?.name || !editing?.slug) {
      toast.error("Name and slug are required");
      return;
    }
    try {
      if (editing.id) {
        await updatePlan({ id: editing.id, data: editing }).unwrap();
        toast.success("Subscription plan updated");
      } else {
        await createPlan(editing).unwrap();
        toast.success("Subscription plan created");
      }
      setDialogOpen(false);
      setEditing(null);
    } catch (saveError: any) {
      toast.error(saveError?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deletePlan(id).unwrap();
      toast.success("Subscription plan deleted");
    } catch (deleteError: any) {
      toast.error(deleteError?.data?.detail || "Failed to delete");
    }
  };

  const set = (field: string, value: any) =>
    setEditing((previous) => (previous ? { ...previous, [field]: value } : previous));

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Subscription Plans</h3>
          <p className="text-sm text-muted-foreground">Feature access, per-seat pricing, and included allowances.</p>
        </div>
        <Button onClick={openCreate}>New Plan</Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Outbound Min</TableHead>
            <TableHead>Outbound SMS</TableHead>
            <TableHead>Features</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {plans?.map((plan) => (
            <TableRow key={plan.id} className="cursor-pointer" onClick={() => openEdit(plan)}>
              <TableCell className="font-medium">
                {plan.name} <span className="text-muted-foreground text-xs">({plan.slug})</span>
              </TableCell>
              <TableCell>
                {plan.pricingMode === "AT_COST" ? (
                  <>
                    <span className="mr-2 text-muted-foreground line-through">${plan.monthlyBasePrice}/mo</span>
                    $0/mo
                    <span className="ml-1 text-xs text-muted-foreground">100% off, add-ons at cost</span>
                  </>
                ) : (
                  <>${plan.monthlyBasePrice}/mo</>
                )}
              </TableCell>
              <TableCell>{plan.includedOutboundCallMinutes}/seat</TableCell>
              <TableCell>{plan.includedOutboundSms}/seat</TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs">
                  {(plan.features ?? []).length} features
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant={plan.isActive ? "default" : "secondary"}>
                    {plan.isActive ? "Active" : "Inactive"}
                  </Badge>
                  {/* Worth seeing at a glance: this is the plan every trial is charged and
                      allowanced against, so changing it changes what trials get. */}
                  {plan.isDefault && (
                    <Badge variant="outline" className="text-xs">Trial default</Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={(clickEvent) => clickEvent.stopPropagation()}
                    >
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent onClick={(clickEvent) => clickEvent.stopPropagation()}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete the {plan.name} plan?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently removes the "{plan.name}" ({plan.slug}) subscription plan. Organisations on it
                        will be left without a plan. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel onClick={(clickEvent) => clickEvent.stopPropagation()}>
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={(clickEvent) => {
                          clickEvent.stopPropagation();
                          handleDelete(plan.id);
                        }}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </TableCell>
            </TableRow>
          ))}
          {(!plans || plans.length === 0) && (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground">
                No subscription plans
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogTitle>{editing?.id ? "Edit" : "New"} Subscription Plan</DialogTitle>
          <DialogDescription>Configure tier features, pricing, and the allowances included with the plan.</DialogDescription>
          {editing && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input value={editing.name ?? ""} onChange={(changeEvent) => set("name", changeEvent.target.value)} placeholder="Pro" />
                </div>
                <div className="space-y-1.5">
                  <Label>Slug</Label>
                  <Input value={editing.slug ?? ""} onChange={(changeEvent) => set("slug", changeEvent.target.value)} placeholder="pro" />
                </div>
                <div className="space-y-1.5">
                  <Label>Price/seat/mo ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editing.perSeatMonthlyPrice ?? ""}
                    onChange={(changeEvent) => set("perSeatMonthlyPrice", parseFloat(changeEvent.target.value) || 0)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Stripe Price ID</Label>
                  <Input
                    value={editing.stripePriceId ?? ""}
                    onChange={(changeEvent) => set("stripePriceId", changeEvent.target.value || null)}
                    placeholder="price_..."
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={editing.sortOrder ?? 0}
                    onChange={(changeEvent) => set("sortOrder", parseInt(changeEvent.target.value) || 0)}
                  />
                </div>
              </div>

              <div className="border rounded-lg p-3 space-y-3">
                <p className="text-sm font-medium">Plan price</p>
                {editing.pricingMode === "AT_COST" && (
                  <p className="text-xs text-muted-foreground">
                    Organisations see the monthly price as the list price with a 100% discount, so they pay $0 for the
                    plan. Add-ons are discounted to our cost and usage bills at the plan's usage rates.
                  </p>
                )}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Monthly price ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editing.monthlyBasePrice ?? 0}
                      onChange={(changeEvent) => set("monthlyBasePrice", parseFloat(changeEvent.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">
                      Annual price ($) <span className="text-muted-foreground">(empty = monthly only)</span>
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editing.annualBasePrice ?? ""}
                      onChange={(changeEvent) =>
                        set("annualBasePrice", changeEvent.target.value ? parseFloat(changeEvent.target.value) : null)
                      }
                      placeholder="Monthly only"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Stripe lookup key</Label>
                    <Input
                      value={editing.stripeLookupKey ?? ""}
                      onChange={(changeEvent) => set("stripeLookupKey", changeEvent.target.value || null)}
                      placeholder="pro_monthly"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Visibility</Label>
                    <Select
                      value={editing.visibility ?? "PUBLIC"}
                      onValueChange={(nextVisibility) => set("visibility", nextVisibility as PlanVisibility)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PUBLIC">Public</SelectItem>
                        <SelectItem value="PRIVATE">Private, by invitation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Pricing mode</Label>
                    <Select
                      value={editing.pricingMode ?? "LIST"}
                      onValueChange={(nextMode) => set("pricingMode", nextMode as PlanPricingMode)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LIST">List price</SelectItem>
                        <SelectItem value="AT_COST">At cost, plan and add-ons discounted to our cost</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Usage rates</Label>
                    <Select
                      value={editing.pricingPlanId ? String(editing.pricingPlanId) : DEFAULT_USAGE_RATES}
                      onValueChange={(nextPlanId) =>
                        set("pricingPlanId", nextPlanId === DEFAULT_USAGE_RATES ? null : parseInt(nextPlanId))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={DEFAULT_USAGE_RATES}>Default usage rates</SelectItem>
                        {(pricingPlans ?? [])
                          .filter((pricingPlan) => !pricingPlan.name.startsWith("Custom pricing for "))
                          .map((pricingPlan) => (
                            <SelectItem key={pricingPlan.id} value={String(pricingPlan.id)}>
                              {pricingPlan.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-3 space-y-3">
                <p className="text-sm font-medium">Seats</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Included seats</Label>
                    <Input
                      type="number"
                      value={editing.includedSeats ?? 1}
                      onChange={(changeEvent) => set("includedSeats", parseInt(changeEvent.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Extra seat price ($/mo)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editing.extraSeatPrice ?? 0}
                      onChange={(changeEvent) => set("extraSeatPrice", parseFloat(changeEvent.target.value) || 0)}
                    />
                  </div>
                  <div className="flex items-center justify-between pt-5">
                    <Label className="text-xs">Unlimited seats</Label>
                    <Switch
                      checked={!!editing.unlimitedSeats}
                      onCheckedChange={(isUnlimited) => set("unlimitedSeats", isUnlimited)}
                    />
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-3 space-y-3">
                <p className="text-sm font-medium">Included Allowances (per plan)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Outbound call minutes</Label>
                    <Input
                      type="number"
                      value={editing.includedOutboundCallMinutes ?? 0}
                      onChange={(changeEvent) => set("includedOutboundCallMinutes", parseInt(changeEvent.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">
                      Inbound call minutes <span className="text-muted-foreground">(empty = unlimited)</span>
                    </Label>
                    <Input
                      type="number"
                      value={editing.includedInboundCallMinutes ?? ""}
                      onChange={(changeEvent) =>
                        set("includedInboundCallMinutes", changeEvent.target.value ? parseInt(changeEvent.target.value) : null)
                      }
                      placeholder="Unlimited"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Outbound SMS</Label>
                    <Input
                      type="number"
                      value={editing.includedOutboundSms ?? 0}
                      onChange={(changeEvent) => set("includedOutboundSms", parseInt(changeEvent.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">
                      Inbound SMS <span className="text-muted-foreground">(empty = unlimited)</span>
                    </Label>
                    <Input
                      type="number"
                      value={editing.includedInboundSms ?? ""}
                      onChange={(changeEvent) => set("includedInboundSms", changeEvent.target.value ? parseInt(changeEvent.target.value) : null)}
                      placeholder="Unlimited"
                    />
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-3 space-y-3">
                <p className="text-sm font-medium">Trial Allowances (flat, not per-seat)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Outbound call minutes</Label>
                    <Input
                      type="number"
                      value={editing.trialOutboundCallMinutes ?? 50}
                      onChange={(changeEvent) => set("trialOutboundCallMinutes", parseInt(changeEvent.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Outbound SMS</Label>
                    <Input
                      type="number"
                      value={editing.trialOutboundSms ?? 20}
                      onChange={(changeEvent) => set("trialOutboundSms", parseInt(changeEvent.target.value) || 0)}
                    />
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-3 space-y-3">
                <p className="text-sm font-medium">Status</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between">
                    <Label>Active</Label>
                    <Switch
                      checked={editing.isActive}
                      onCheckedChange={(isEnabled) => set("isActive", isEnabled)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    {/* Trials have no plan of their own, so their allowances come from whichever
                        plan is marked default. Marking one here clears the previous default. */}
                    <Label>Default for trials</Label>
                    <Switch
                      checked={editing.isDefault}
                      onCheckedChange={(isEnabled) => set("isDefault", isEnabled)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">
                      Included phone numbers <span className="text-muted-foreground">(empty = all included)</span>
                    </Label>
                    <Input
                      type="number"
                      value={editing.includedPhoneNumbers ?? ""}
                      onChange={(changeEvent) => set("includedPhoneNumbers", changeEvent.target.value ? parseInt(changeEvent.target.value) : null)}
                      placeholder="All included"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">
                      Max contact lists <span className="text-muted-foreground">(empty = unlimited)</span>
                    </Label>
                    <Input
                      type="number"
                      value={editing.maxContactLists ?? ""}
                      onChange={(changeEvent) => set("maxContactLists", changeEvent.target.value ? parseInt(changeEvent.target.value) : null)}
                      placeholder="Unlimited"
                    />
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-3 space-y-3">
                <div>
                  <p className="text-sm font-medium">Included features</p>
                  <p className="text-xs text-muted-foreground">
                    Everything ticked here is unlocked for any organisation on this plan.
                  </p>
                </div>
                <div className="max-h-72 space-y-3 overflow-y-auto">
                  {Object.entries(featureOptionsByCategory).map(([category, options]) => (
                    <div key={category} className="space-y-1">
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{category}</p>
                      {options.map((option) => (
                        <label
                          key={option.slug}
                          className="flex cursor-pointer items-start gap-2 rounded p-1 hover:bg-muted"
                        >
                          <Checkbox
                            className="mt-0.5"
                            checked={(editing.features ?? []).includes(option.slug)}
                            onCheckedChange={(isIncluded) =>
                              set(
                                "features",
                                isIncluded
                                  ? [...(editing.features ?? []), option.slug]
                                  : (editing.features ?? []).filter((slug) => slug !== option.slug),
                              )
                            }
                          />
                          <span className="text-sm leading-tight">
                            {option.displayName}
                            <span className="block text-xs text-muted-foreground">{option.description}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="border rounded-lg p-3 space-y-3">
                <p className="text-sm font-medium">AI Agents</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Included AI minutes (per plan)</Label>
                    <Input
                      type="number"
                      value={editing.includedAiAgentMinutes ?? 0}
                      onChange={(changeEvent) => set("includedAiAgentMinutes", parseInt(changeEvent.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Trial AI minutes (flat)</Label>
                    <Input
                      type="number"
                      value={editing.trialAiAgentMinutes ?? 5}
                      onChange={(changeEvent) => set("trialAiAgentMinutes", parseInt(changeEvent.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">
                      Max agents <span className="text-muted-foreground">(empty = unlimited)</span>
                    </Label>
                    <Input
                      type="number"
                      value={editing.maxAiAgents ?? ""}
                      onChange={(changeEvent) => set("maxAiAgents", changeEvent.target.value ? parseInt(changeEvent.target.value) : null)}
                      placeholder="Unlimited"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
